import type { RoleBadge } from '@stfors/shared-types';
import { useLookups, activeOptionsFor } from '../api/queries/lookups';
import './RolePills.css';

/** Backend-backed directory roles.
 *  - client / vendor / contractor → role tables
 *  - contact → individual only (organization_contacts)
 *  Other lookup values can be renamed/reordered but stay inert for assignment. */
const ASSIGNABLE_ROLES = new Set<string>(['client', 'vendor', 'contractor', 'contact']);
/** Contact is individual-only (a person linked to a company). */
const COMPANY_ROLES = new Set<string>(['client', 'vendor', 'contractor']);

export function isAssignableRole(value: string): value is RoleBadge {
  return ASSIGNABLE_ROLES.has(value);
}

export type DirectoryRoleScope = 'individual' | 'company';

/**
 * Active Directory Roles from Settings → Lookups (`contact_role`), limited to
 * values the API can actually attach. Labels + sort_order come from the lookup.
 * `scope: 'company'` hides individual-only roles (contact).
 */
export function useDirectoryRoleOptions(
  scope: DirectoryRoleScope = 'individual',
): { value: RoleBadge; label: string }[] {
  const lookups = useLookups('contact_role');
  const allowed = scope === 'company' ? COMPANY_ROLES : ASSIGNABLE_ROLES;
  const active = activeOptionsFor(lookups.data?.lookup_options, 'contact_role')
    .filter((o) => allowed.has(o.value) && isAssignableRole(o.value))
    .map((o) => ({
      value: o.value as RoleBadge,
      label: (o.label && o.label.trim()) || o.value,
    }));

  if (active.length > 0) return active;

  // Fallback while loading or if every option was deactivated.
  const fallback: { value: RoleBadge; label: string }[] = [
    { value: 'client', label: 'Client' },
    { value: 'vendor', label: 'Vendor' },
    { value: 'contractor', label: 'Contractor' },
  ];
  if (scope === 'individual') fallback.push({ value: 'contact', label: 'Contact' });
  return fallback;
}

interface RolePillsProps {
  /** Currently selected roles. */
  value: RoleBadge[];
  onChange: (next: RoleBadge[]) => void;
  disabled?: boolean;
  /** 'company' hides individual-only roles (Contact). Default individual. */
  scope?: DirectoryRoleScope;
  /** Render an extra "Staff" pill (a login account, not a business role).
   *  Only pass this for users with settings.manage_users. */
  showStaff?: boolean;
  staffSelected?: boolean;
  onStaffToggle?: (next: boolean) => void;
}

/**
 * Multi-select role picker rendered as shaded pills (not checkboxes):
 * selected pills fill with the role's brand tint, unselected pills are a
 * faint outline that hints the role color on hover. Colors match the
 * directory role badges (.role-client / -vendor / -contractor).
 *
 * Options come from Settings → Lookups → Directory Roles (`contact_role`).
 */
export function RolePills({
  value,
  onChange,
  disabled = false,
  scope = 'individual',
  showStaff = false,
  staffSelected = false,
  onStaffToggle,
}: RolePillsProps): JSX.Element {
  const roles = useDirectoryRoleOptions(scope);

  const toggle = (role: RoleBadge) => {
    if (disabled) return;
    onChange(value.includes(role) ? value.filter((r) => r !== role) : [...value, role]);
  };

  return (
    <div className="role-pills" role="group" aria-label="Directory roles">
      {roles.map(({ value: role, label }) => {
        const selected = value.includes(role);
        return (
          <button
            key={role}
            type="button"
            className={`role-pill role-pill-${role}${selected ? ' role-pill-selected' : ''}`}
            aria-pressed={selected}
            onClick={() => toggle(role)}
            disabled={disabled}
          >
            {label}
          </button>
        );
      })}
      {showStaff && (
        <button
          type="button"
          className={`role-pill role-pill-staff${staffSelected ? ' role-pill-selected' : ''}`}
          aria-pressed={staffSelected}
          onClick={() => !disabled && onStaffToggle?.(!staffSelected)}
          disabled={disabled}
        >
          Staff
        </button>
      )}
    </div>
  );
}
